import {Section} from 'common/components/items-menu/types';
import {ProjectFeatureFlag, REPORT_BASICS_LINKS, ReportName} from 'common/report/constants';
import {ProjectDetails} from 'common/report/stores/project-details';
import {isBoardsUiEnabled} from 'common/report/util/boards';
import {TourId} from 'common/util/chameleon';

import {HelpOnboardingSectionItemValue, NavReportConfig} from './types';

// make sure these values are in sync with common.styl's $chrome-header-height(s)
export const chromeHeaderPrimaryHeight = 60;
const chromeHeaderSecondaryHeight = 50;
export const chromeHeaderHeight = chromeHeaderPrimaryHeight + chromeHeaderSecondaryHeight;
export const globalSearchBrowseReports = `Browse all Reports & Dashboards`;

export enum HelpLabels {
  AskQuestions = `Ask Questions`,
  CommunityWidget = `Community FAQs`,
  ContactSupport = `Contact Support`,
  DemoProject = `Mixpanel Demo Project`,
  DevDocs = `Developer Documentation`,
  GettingStarted = `Getting Started`,
  Intro = `Quick Start`,
  LiveChat = `Live Chat`,
  ProductUpdates = `What's New`,
  Community = `Visit Community`,
  ZendeskHelp = `Search Help Center`,
}

export enum HelpTrackingEvent {
  CommunityWidget = `[Help Widget] Community Widget`,
  DevDocs = `[Help Widget] Developer Documentation`,
  ExternalLink = `[Help Widget] External Link`,
  Intro = `[Help Widget] Learn Mixpanel Basics`,
  GettingStarted = `[Help Widget] Getting Started`,
  LiveChat = `[Help Widget] Live Chat Started`,
  Community = `[Help Widget] Visit Community`,
  Support = `[Help Widget] Support`,
  ZendeskHelp = `[Help Widget] Search Help Docs`,
}

export enum HelpGettingStartedLabels {
  Dashboards = `Dashboards`,
  Insights = `Insights`,
  Funnels = `Funnels`,
  Flows = `Flows`,
  Retention = `Retention`,
}

export const CHAMELEON_TOUR_QUERY_PARAM = `chmlnTour`;

export const HELP_GETTING_STARTED_SECTIONS: Array<Section<HelpOnboardingSectionItemValue>> = [
  {
    items: [
      {
        label: HelpLabels.Intro,
        icon: `brand-mixpanel`,
      },
      {
        label: HelpGettingStartedLabels.Dashboards,
        icon: `brand-dashboard`,
        value: {
          helpLink: REPORT_BASICS_LINKS[ReportName.Dashboards],
          reportName: ReportName.Dashboards,
          isOnboardingItem: true,
        },
      },
      {
        label: HelpGettingStartedLabels.Insights,
        icon: `brand-insights`,
        value: {
          helpLink: REPORT_BASICS_LINKS[ReportName.Insights],
          tourId: TourId.InsightsNewQueryBuilder,
          reportName: ReportName.Insights,
          isOnboardingItem: true,
        },
      },
      {
        label: HelpGettingStartedLabels.Funnels,
        icon: `brand-funnels`,
        value: {
          helpLink: REPORT_BASICS_LINKS[ReportName.Funnels],
          reportName: ReportName.Funnels,
          isOnboardingItem: true,
        },
      },
      {
        label: HelpGettingStartedLabels.Flows,
        icon: `brand-flows`,
        value: {
          helpLink: REPORT_BASICS_LINKS[ReportName.Flows],
          tourId: TourId.Flows,
          reportName: ReportName.Flows,
          isOnboardingItem: true,
        },
      },
      {
        label: HelpGettingStartedLabels.Retention,
        icon: `brand-retention`,
        value: {
          helpLink: REPORT_BASICS_LINKS[ReportName.Retention],
          tourId: TourId.Retention,
          reportName: ReportName.Retention,
          isOnboardingItem: true,
        },
      },
    ],
  },
];

export const ALL_PRODUCT_UPDATES_LINK = `https://community.mixpanel.com/product-updates`;

export enum NavCategoryType {
  Analysis = `Analysis`,
  Applications = `Applications`,
  Dashboards = `Dashboards`,
  DataManagement = `Data Management`,
  MessagesAndExperiments = `Messages & Experiments`,
  Project = `Project`,
  Settings = `Settings`,
  Users = `Users`,
}

// remove this and fill this out in REPORT_INFO for the report if it is used in full display in the primary nav
const UNUSED_NAVIGATION_REPORT_INFO = {
  description: ``,
  icon: ``,
  label: ``,
};

// if you need to change it, set both this and the one in the backend with the same name
export const NEW_LIVEVIEW_PERCENTAGE = 0;

function showNewLive(project: ProjectDetails) {
  return project?.hasFeatureFlag(ProjectFeatureFlag.LiveviewNewUI) || project?.id % 100 < NEW_LIVEVIEW_PERCENTAGE;
}

export const REPORT_INFO: {[key in ReportName]?: NavReportConfig} = {
  [ReportName.Dashboards]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `dashboards`,
    endpointAliases: [`dashboard`],
    category: () => NavCategoryType.Dashboards,
  },
  [ReportName.Insights]: {
    description: `Explore your data by segmenting and filtering event and user counts`,
    endpoint: () => `insights`,
    icon: `brand-insights`,
    label: `Insights`,
    category: () => NavCategoryType.Analysis,
  },
  [ReportName.Flows]: {
    description: `Analyze how users move through your product by exploring paths between key actions`,
    icon: `brand-flows`,
    label: `Flows`,
    endpoint: () => `flows`,
    category: () => NavCategoryType.Analysis,
  },
  [ReportName.Funnels]: {
    description: `Understand the conversion and drop-off of critical sequences by measuring user progress at each step`,
    icon: `brand-funnels`,
    label: `Funnels`,
    endpoint: () => `funnels`,
    category: () => NavCategoryType.Analysis,
  },
  [ReportName.Retention]: {
    description: `Assess stickiness of your product by identifying how many users come back over time`,
    icon: `brand-retention`,
    label: `Retention`,
    endpoint: () => `retention`,
    category: () => NavCategoryType.Analysis,
  },
  [ReportName.Signal]: {
    description: `Uncover insights by finding correlations between user actions`,
    icon: `brand-signal`,
    label: `Signal`,
    endpoint: () => `signal`,
    category: (reportContext) =>
      isBoardsUiEnabled(reportContext.project) ? NavCategoryType.Applications : NavCategoryType.Analysis,
  },
  [ReportName.Impact]: {
    description: `Determine the impact of a new feature by comparing behavior before and after launch`,
    icon: `brand-impact`,
    label: `Impact`,
    endpoint: () => `impact`,
    category: (reportContext) =>
      isBoardsUiEnabled(reportContext.project) ? NavCategoryType.Applications : NavCategoryType.Analysis,
  },
  [ReportName.ExperimentReporting]: {
    beta: true,
    description: `View an A/B test's effect by comparing metrics between test variants`,
    icon: `brand-experiments`,
    label: `Experiments`,
    endpoint: () => `experiment-reporting`,
    category: (reportContext) =>
      isBoardsUiEnabled(reportContext.project) ? NavCategoryType.Applications : NavCategoryType.Analysis,
  },
  [ReportName.Explore]: {
    description: `Understand each user`,
    icon: `brand-explore`,
    label: `Explore`,
    endpoint: () => `explore`,
    endpointAliases: [`profile`],
    category: () => NavCategoryType.Users,
  },
  [ReportName.Profile]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `profile`,
    category: () => NavCategoryType.Users,
  },
  [ReportName.Campaign]: {
    description: `Create and send a series of targeted messages`,
    endpoint: () => `triggers/campaign`,
    icon: `feature-tour`,
    label: `Campaign`,
    // stop showing "campaign" tab to new projects unless they have explicitly asked for it
    hidden: (reportContext): boolean =>
      (reportContext.project?.id > 1_984_369 &&
        !reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.NotifsCampaigns)) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [ReportName.Messages]: {
    description: `Re-engage customers with targeted messages`,
    endpoint: () => `triggers/messages`,
    icon: `brand-messages`,
    label: `Messages`,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [ReportName.Journeys]: {
    beta: true,
    description: `Plan and execute user journeys`,
    endpoint: () => `journeys`,
    icon: `brand-journeys`,
    label: `Journeys`,
    hidden: (reportContext): boolean => !reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.NotifsVisualJourneys),
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [ReportName.AbTest]: {
    description: `Experiment to find the optimal user experiences`,
    endpoint: () => `ab_test`,
    icon: `brand-abtest`,
    label: `A/B Testing`,
    category: () => NavCategoryType.MessagesAndExperiments,
  },
  [ReportName.Lexicon]: {
    description: `Document and transform your events and properties`,
    icon: `brand-lexicon`,
    label: `Lexicon`,
    endpoint: () => `lexicon#transformations`,
    category: () => NavCategoryType.DataManagement,
  },
  [ReportName.DataAudit]: {
    description: `View possible issues with your implementation`,
    icon: `brand-audit`,
    label: `Data Audit`,
    beta: true,
    endpoint: () => `lexicon#audit`,
    hidden: (reportContext): boolean =>
      !reportContext.isProjectOrGlobalWorkspace ||
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportDataAudit),
    category: () => NavCategoryType.DataManagement,
  },
  [ReportName.Live]: {
    description: `View a live streaming feed of events`,
    endpoint: () => `live`,
    icon: `brand-liveview`,
    label: `Live View`,
    category: () => NavCategoryType.DataManagement,
  },
  [ReportName.Inspector]: {
    beta: true,
    description: `View your raw events`,
    endpoint: () => `inspector`,
    icon: `brand-inspector`,
    label: `Inspector`,
    hidden: (reportContext): boolean =>
      showNewLive(reportContext.project) || !reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.InspectorApp),
    category: () => NavCategoryType.DataManagement,
  },
  [ReportName.Cohorts]: {
    description: `Manage your saved cohorts`,
    icon: `brand-cohorts`,
    label: `Cohorts`,
    endpoint: (reportContext): string =>
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.DisableEntityManagementCohorts)
        ? `cohorts`
        : `entity-management#~(entityType~'cohorts)`,
    category: (): NavCategoryType => NavCategoryType.DataManagement,
  },
  [ReportName.Integrations]: {
    description: `Connect Mixpanel to a data pipeline or customer engagement tool`,
    icon: `brand-integrations`,
    label: `Integrations`,
    endpoint: () => `integrations`,
    hidden: (reportContext): boolean => !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.DataManagement,
  },
  [ReportName.JqlConsole]: {
    label: `JQL`,
    description: `Write powerful queries to analyze your data in Mixpanel.`,
    icon: ``,
    endpoint: () => `jql-console`,
    // JQL would allow users to bypass data filters, it should be hidden for workspaces
    hidden: (reportContext): boolean =>
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportJqlConsole) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.Applications,
  },
  [ReportName.Segmentation]: {
    label: `Segmentation`,
    description: `Measure the actions that people take.`,
    icon: ``,
    endpoint: () => `segmentation`,
    hidden: (reportContext) =>
      reportContext.project?.id > 1_894_797 ||
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportSegmentation),
    category: () => NavCategoryType.Applications,
  },
  [ReportName.Revenue]: {
    label: `Revenue`,
    description: `Easily understand and analyze the revenue you make from individual customers.`,
    icon: ``,
    endpoint: () => `revenue`,
    hidden: (reportContext): boolean =>
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportRevenue) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: () => NavCategoryType.Applications,
  },
  [ReportName.MpPlatform]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `mpplatform`,
    category: () => NavCategoryType.Applications,
  },
  [ReportName.UserSettings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings/account`,
    category: () => NavCategoryType.Settings,
  },
  [ReportName.Settings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings`,
    category: () => NavCategoryType.Settings,
  },
  [ReportName.Setup]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `setup`,
    category: () => NavCategoryType.Settings,
  },
  [ReportName.Views]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `views`,
    category: () => NavCategoryType.Project,
  },
};
